import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ParentLayout from '../components/ParentLayout';
import { DollarSign, TrendingUp, TrendingDown, Download, Plus, Search, Filter, Calendar, Eye, Trash2, Edit2, FileText, Receipt, X, ChevronDown, AlertCircle, Upload, Paperclip, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXPENSE_CATEGORIES = [
  { value: 'equipment', label: 'Equipment' },
  { value: 'uniforms', label: 'Uniforms' },
  { value: 'field_rental', label: 'Field Rental' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'coaching', label: 'Coaching' },
  { value: 'training', label: 'Training' },
  { value: 'travel', label: 'Travel' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'administrative', label: 'Administrative' },
  { value: 'fundraising', label: 'Fundraising' },
  { value: 'facilities', label: 'Facilities' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'other', label: 'Other' }
];

const DONATION_SOURCES = [
  { value: 'zeffy', label: 'Zeffy' },
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'other', label: 'Other' }
];

export default function AdminFinancesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Data state
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter state
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Modal state
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddDonation, setShowAddDonation] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreviewType, setEmailPreviewType] = useState('receipt');
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  
  // Form state
  const [expenseForm, setExpenseForm] = useState({
    description: '',
    amount: '',
    category: 'other',
    vendor: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [donationForm, setDonationForm] = useState({
    donor_name: '',
    donor_email: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    source: 'zeffy',
    notes: '',
    is_anonymous: false
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(null);

  const fetchTransactions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (activeTab !== 'all') params.append('transaction_type', activeTab);
      if (searchQuery) params.append('search', searchQuery);
      if (dateRange.start) params.append('start_date', dateRange.start + 'T00:00:00Z');
      if (dateRange.end) params.append('end_date', dateRange.end + 'T23:59:59Z');
      if (selectedCategory) params.append('category', selectedCategory);
      
      const res = await fetch(`${API_URL}/api/admin/finances/transactions?${params}`, {
        credentials: 'include'
      });
      
      if (res.status === 403) {
        setAccessDenied(true);
        return;
      }
      
      if (!res.ok) throw new Error('Failed to fetch transactions');
      
      const data = await res.json();
      setTransactions(data.transactions || []);
      setTotalCount(data.total || 0);
    } catch (err) {
      setError(err.message);
    }
  }, [activeTab, searchQuery, dateRange, selectedCategory]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/finances/summary?year=${selectedYear}`, {
        credentials: 'include'
      });
      
      if (res.status === 403) return;
      if (!res.ok) throw new Error('Failed to fetch summary');
      
      const data = await res.json();
      setSummary(data);
    } catch (err) {
      console.error('Summary error:', err);
    }
  }, [selectedYear]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTransactions(), fetchSummary()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTransactions, fetchSummary]);

  const handleAddExpense = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/finances/expenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...expenseForm,
          amount: parseFloat(expenseForm.amount),
          date: expenseForm.date + 'T12:00:00Z'
        })
      });
      
      if (!res.ok) throw new Error('Failed to add expense');
      
      const data = await res.json();
      
      // Upload receipt if file selected
      if (receiptFile && data.expense_id) {
        const formData = new FormData();
        formData.append('file', receiptFile);
        
        await fetch(`${API_URL}/api/admin/finances/expenses/${data.expense_id}/receipt`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
      }
      
      setShowAddExpense(false);
      setExpenseForm({
        description: '',
        amount: '',
        category: 'other',
        vendor: '',
        date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      setReceiptFile(null);
      fetchTransactions();
      fetchSummary();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleAddDonation = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/finances/donations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...donationForm,
          amount: parseFloat(donationForm.amount),
          date: donationForm.date + 'T12:00:00Z'
        })
      });
      
      if (!res.ok) throw new Error('Failed to add donation');
      
      setShowAddDonation(false);
      setDonationForm({
        donor_name: '',
        donor_email: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        source: 'zeffy',
        notes: '',
        is_anonymous: false
      });
      fetchTransactions();
      fetchSummary();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDeleteTransaction = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      const endpoint = type === 'expense' 
        ? `${API_URL}/api/admin/finances/expenses/${id}`
        : `${API_URL}/api/admin/finances/donations/${id}`;
      
      const res = await fetch(endpoint, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!res.ok) throw new Error('Failed to delete');
      
      fetchTransactions();
      fetchSummary();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleUploadReceipt = async (expenseId, file) => {
    setUploadingReceipt(expenseId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/admin/finances/expenses/${expenseId}/receipt`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!res.ok) throw new Error('Failed to upload receipt');
      
      fetchTransactions();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setUploadingReceipt(null);
    }
  };

  const handleDeleteReceipt = async (expenseId) => {
    if (!window.confirm('Delete this receipt?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/finances/expenses/${expenseId}/receipt`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!res.ok) throw new Error('Failed to delete receipt');
      
      fetchTransactions();
      setShowReceiptModal(null);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleExportCSV = () => {
    window.open(`${API_URL}/api/admin/finances/export/csv?year=${selectedYear}`, '_blank');
  };

  const handleExportPDF = () => {
    window.open(`${API_URL}/api/admin/finances/export/pdf?year=${selectedYear}`, '_blank');
  };

  const handleEmailPreview = async (type) => {
    setEmailPreviewType(type);
    try {
      const res = await fetch(`${API_URL}/api/admin/email-previews/${type}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to load preview');
      const data = await res.json();
      setEmailPreviewHtml(data.html);
      setShowEmailPreview(true);
    } catch (err) {
      alert('Error loading preview: ' + err.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCategoryLabel = (cat) => {
    const found = EXPENSE_CATEGORIES.find(c => c.value === cat);
    return found ? found.label : cat;
  };

  if (accessDenied) {
    return (
      <ParentLayout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-6">
            Finance access requires Super Admin or President permissions.
          </p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="px-6 py-2 bg-navy-700 text-white rounded-lg hover:bg-navy-800"
          >
            Back to Dashboard
          </button>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="mb-8">
          <p className="text-[#fae36c] text-sm font-bold tracking-widest uppercase mb-1">TREASURY</p>
          <h1 className="text-3xl md:text-4xl font-display text-[#32425c] uppercase tracking-wide">
            FINANCIAL MANAGEMENT
          </h1>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <TrendingUp className="w-8 h-8 text-green-500" />
                <span className="text-xs text-gray-500 uppercase font-semibold">Income</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.income?.total)}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.registration_count} registrations</p>
            </div>
            
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <TrendingDown className="w-8 h-8 text-red-500" />
                <span className="text-xs text-gray-500 uppercase font-semibold">Expenses</span>
              </div>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.expenses?.total)}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.expense_count} entries</p>
            </div>
            
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <DollarSign className="w-8 h-8 text-purple-500" />
                <span className="text-xs text-gray-500 uppercase font-semibold">Donations</span>
              </div>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(summary.income?.donations)}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.donation_count} donations</p>
            </div>
            
            <div className={`bg-white rounded-xl p-5 shadow-sm border-2 ${summary.net_income >= 0 ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-center justify-between mb-3">
                <DollarSign className={`w-8 h-8 ${summary.net_income >= 0 ? 'text-green-600' : 'text-red-600'}`} />
                <span className="text-xs text-gray-600 uppercase font-semibold">Net Income</span>
              </div>
              <p className={`text-2xl font-bold ${summary.net_income >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {formatCurrency(summary.net_income)}
              </p>
              <p className="text-xs text-gray-500 mt-1">{selectedYear} YTD</p>
            </div>
          </div>
        )}

        {/* Actions Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAddExpense(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-semibold"
              >
                <Plus className="w-4 h-4" />
                Add Expense
              </button>
              <button
                onClick={() => setShowAddDonation(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-semibold"
              >
                <Plus className="w-4 h-4" />
                Add Donation
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                {[2024, 2025, 2026, 2027].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                CSV
              </button>
              <button
                onClick={handleExportPDF}
                className="flex items-center gap-2 px-4 py-2 bg-[#32425c] text-white rounded-lg hover:bg-[#263347] transition text-sm font-medium"
              >
                <FileText className="w-4 h-4" />
                PDF Report
              </button>
            </div>
          </div>
        </div>

        {/* Email Preview Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Email Template Previews
          </h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleEmailPreview('receipt')}
              className="px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition"
            >
              Payment Receipt
            </button>
            <button
              onClick={() => handleEmailPreview('reminder_overdue')}
              className="px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition"
            >
              Overdue Task Reminder
            </button>
            <button
              onClick={() => handleEmailPreview('reminder_today')}
              className="px-3 py-2 bg-orange-100 text-orange-700 rounded-lg text-sm font-medium hover:bg-orange-200 transition"
            >
              Due Today Reminder
            </button>
            <button
              onClick={() => handleEmailPreview('invite')}
              className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition"
            >
              Parent Invitation
            </button>
          </div>
        </div>

        {/* Filters & Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Tabs */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              {[
                { key: 'all', label: 'All' },
                { key: 'income', label: 'Income' },
                { key: 'expense', label: 'Expenses' },
                { key: 'donation', label: 'Donations' }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                    activeTab === tab.key
                      ? 'bg-[#32425c] text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
            
            {/* Date Range */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            
            {activeTab === 'expense' && (
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All Categories</option>
                {EXPENSE_CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading transactions...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-500">{error}</div>
          ) : transactions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No transactions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {transactions.map((t, idx) => (
                    <tr key={t.transaction_id || idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDate(t.date)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          t.type === 'income' ? 'bg-green-100 text-green-700' :
                          t.type === 'expense' ? 'bg-red-100 text-red-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {t.type?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{t.description}</p>
                        {t.vendor && <p className="text-xs text-gray-500">Vendor: {t.vendor}</p>}
                        {t.payment_method && <p className="text-xs text-gray-500">Method: {t.payment_method}</p>}
                        {t.source && <p className="text-xs text-gray-500">Source: {t.source}</p>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {getCategoryLabel(t.category)}
                      </td>
                      <td className={`px-4 py-3 text-sm font-semibold text-right ${
                        t.type === 'expense' ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {t.type === 'expense' ? '-' : '+'}{formatCurrency(t.amount)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {t.type === 'expense' && (
                            <>
                              {t.receipt_url ? (
                                <button
                                  onClick={() => setShowReceiptModal(t)}
                                  className="p-1 text-green-600 hover:text-green-700 transition"
                                  title="View Receipt"
                                >
                                  <Paperclip className="w-4 h-4" />
                                </button>
                              ) : (
                                <label className="p-1 text-gray-400 hover:text-blue-600 transition cursor-pointer" title="Upload Receipt">
                                  <Upload className="w-4 h-4" />
                                  <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    className="hidden"
                                    onChange={(e) => {
                                      if (e.target.files[0]) {
                                        handleUploadReceipt(t.transaction_id, e.target.files[0]);
                                      }
                                    }}
                                    disabled={uploadingReceipt === t.transaction_id}
                                  />
                                </label>
                              )}
                            </>
                          )}
                          {(t.type === 'expense' || t.type === 'donation') && (
                            <button
                              onClick={() => handleDeleteTransaction(t.type, t.transaction_id)}
                              className="p-1 text-gray-400 hover:text-red-600 transition"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {totalCount > 0 && (
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
              Showing {transactions.length} of {totalCount} transactions
            </div>
          )}
        </div>
      </div>

      {/* Add Expense Modal */}
      {showAddExpense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Add Expense</h2>
              <button onClick={() => setShowAddExpense(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddExpense} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <input
                  type="text"
                  required
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Football helmets"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    required
                    value={expenseForm.date}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  value={expenseForm.category}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {EXPENSE_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                <input
                  type="text"
                  value={expenseForm.vendor}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, vendor: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Sports Authority"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={expenseForm.notes}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                  placeholder="Additional notes..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Receipt (optional)</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setReceiptFile(e.target.files[0])}
                    className="hidden"
                    id="receipt-upload"
                  />
                  <label htmlFor="receipt-upload" className="cursor-pointer">
                    {receiptFile ? (
                      <div className="flex items-center justify-center gap-2 text-green-600">
                        <Paperclip className="w-5 h-5" />
                        <span className="text-sm font-medium">{receiptFile.name}</span>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); setReceiptFile(null); }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-gray-500">
                        <Upload className="w-8 h-8 mx-auto mb-2" />
                        <p className="text-sm">Click to upload receipt</p>
                        <p className="text-xs text-gray-400">JPEG, PNG, WebP or PDF (max 10MB)</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddExpense(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {formSubmitting ? 'Saving...' : 'Add Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Donation Modal */}
      {showAddDonation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Record Donation</h2>
              <button onClick={() => setShowAddDonation(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddDonation} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Donor Name *</label>
                <input
                  type="text"
                  required
                  value={donationForm.donor_name}
                  onChange={(e) => setDonationForm(prev => ({ ...prev, donor_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Donor Email</label>
                <input
                  type="email"
                  value={donationForm.donor_email}
                  onChange={(e) => setDonationForm(prev => ({ ...prev, donor_email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="john@example.com"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={donationForm.amount}
                    onChange={(e) => setDonationForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    required
                    value={donationForm.date}
                    onChange={(e) => setDonationForm(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source *</label>
                <select
                  value={donationForm.source}
                  onChange={(e) => setDonationForm(prev => ({ ...prev, source: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {DONATION_SOURCES.map(src => (
                    <option key={src.value} value={src.value}>{src.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={donationForm.notes}
                  onChange={(e) => setDonationForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="anonymous"
                  checked={donationForm.is_anonymous}
                  onChange={(e) => setDonationForm(prev => ({ ...prev, is_anonymous: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                <label htmlFor="anonymous" className="text-sm text-gray-700">Anonymous donation</label>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddDonation(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {formSubmitting ? 'Saving...' : 'Record Donation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Preview Modal */}
      {showEmailPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <h2 className="text-lg font-bold text-gray-900">
                Email Preview: {emailPreviewType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </h2>
              <button onClick={() => setShowEmailPreview(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-gray-100 p-4">
              <div 
                className="bg-white mx-auto max-w-[600px] shadow-lg"
                dangerouslySetInnerHTML={{ __html: emailPreviewHtml }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Receipt View Modal */}
      {showReceiptModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Receipt</h2>
                <p className="text-sm text-gray-500">{showReceiptModal.description}</p>
              </div>
              <button onClick={() => setShowReceiptModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-gray-100 p-4 flex items-center justify-center">
              {showReceiptModal.receipt_url?.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={showReceiptModal.receipt_url}
                  className="w-full h-[60vh] bg-white"
                  title="Receipt PDF"
                />
              ) : (
                <img
                  src={showReceiptModal.receipt_url}
                  alt="Receipt"
                  className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
                />
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <p className="text-sm text-gray-500">
                {showReceiptModal.receipt_filename || 'Receipt'}
              </p>
              <div className="flex gap-2">
                <a
                  href={showReceiptModal.receipt_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open Full Size
                </a>
                <button
                  onClick={() => handleDeleteReceipt(showReceiptModal.transaction_id)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm font-medium"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Receipt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ParentLayout>
  );
}
